import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDiffViewer from 'react-diff-viewer';
import { Modal } from '@framework/ui/elements/Modal';
import type { PreparedViewModelAIAssistantSkillChange } from '@view-model/application/ai/ViewModelAIAssistantSkillChange';
import { buildSkillDefinitionText } from './AIAssistantSkillDefinitionText';

export function AIAssistantSkillChangeConfirmModal({
  prepared,
  onConfirm,
  onCancel,
}: {
  prepared: PreparedViewModelAIAssistantSkillChange;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const nextDefinition = buildSkillDefinitionText(prepared.skill);

  return (
    <Modal isOpen={true} onClose={onCancel} className="flex max-h-[84vh] max-w-4xl flex-col overflow-hidden p-4">
      <div className="min-w-0">
        <h3 className="truncate text-base font-semibold text-gray-900">
          {prepared.operation === 'create'
            ? t('viewModel.aiChat.skillChangeCreateConfirmTitle')
            : t('viewModel.aiChat.skillChangeUpdateConfirmTitle')}
        </h3>
        <div className="mt-1 text-xs text-gray-500">{t('viewModel.aiChat.skillChangeConfirmDescription')}</div>
      </div>

      <div className="mt-4 min-h-0 overflow-auto rounded border border-gray-200 text-xs">
        {prepared.operation === 'create' ? (
          <pre className="whitespace-pre-wrap break-words bg-gray-50 p-3 font-mono text-gray-800">{nextDefinition}</pre>
        ) : (
          <ReactDiffViewer
            oldValue={buildSkillDefinitionText(prepared.before)}
            newValue={nextDefinition}
            splitView={false}
            showDiffOnly={false}
            leftTitle={t('viewModel.aiChat.skillChangeBeforeLabel')}
            rightTitle={t('viewModel.aiChat.skillChangeAfterLabel')}
          />
        )}
      </div>

      <div className="mt-4 flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
        <button
          className="text-xs text-gray-700 hover:text-gray-900 hover:underline disabled:pointer-events-none disabled:opacity-50"
          onClick={onCancel}
          type="button"
        >
          {t('common.action.cancel')}
        </button>
        <button
          className="inline-flex items-center gap-1 rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50"
          onClick={onConfirm}
          type="button"
        >
          {t('common.action.save')}
        </button>
      </div>
    </Modal>
  );
}
